import { Trans } from '@lingui/macro';
import React from 'react';
import styled from 'styled-components';

/**
 * Login page footer: copyright notice plus the support contact details.
 *
 * Transparent rather than opaque, so the background wave keeps running underneath it;
 * the ink colour is dark enough to stay legible where the wave passes behind the text.
 *
 * The landmark role lives on the host rather than on a nested `<footer>`: a `<footer>`
 * that is not a direct child of `<body>` does not map to `contentinfo`.
 */
const FooterHost = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1.5rem;
  min-height: 2.5rem;
  padding-inline: clamp(1rem, 2vw, 2rem);

  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--login-ink);
`;

const Contacts = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const ContactLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover {
    color: var(--login-link-hover);
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Separator = styled.span`
  opacity: 0.5;
`;

interface LoginFooterProps {
  className?: string;
}

export const LoginFooter = ({ className }: LoginFooterProps) => {
  const year = new Date().getFullYear();

  return (
    <FooterHost className={className} role="contentinfo">
      <span>
        <Trans>© {year} Evidend. Alle rechten voorbehouden.</Trans>
      </span>

      <Contacts>
        <ContactLink href="tel:+31717601642">+31 71 7601642</ContactLink>
        <Separator aria-hidden="true">·</Separator>
        <ContactLink href="mailto:evidend@legalmanager.com">evidend@legalmanager.com</ContactLink>
      </Contacts>
    </FooterHost>
  );
};
